/**
 * Stage-scoped enemy outgoing damage escalate.
 * Each assault wave and the boss stage have independent clocks — stacks never
 * carry across wave/phase transitions. Soft≪AA / judgment untouched.
 */

import { raidCatalog } from "../../data/raid-catalog";
import type { RaidBattleState } from "./raid-types";

export type RaidEscalateKnobs = {
  escalateAfterSec: number;
  escalateEverySec: number;
  damageMulPerStack: number;
  /** Optional hard cap; omit / ≤0 = uncapped. */
  maxStacks?: number;
};

const DEFAULT_MUL_PER_STACK = 0.1;

type EscalateRaw = {
  escalateAfterSec?: number;
  escalateEverySec?: number;
  damageMulPerStack?: number;
  maxStacks?: number;
};

function parseEscalateKnobs(raw: unknown): RaidEscalateKnobs | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as EscalateRaw;
  const after = Number(o.escalateAfterSec);
  const every = Number(o.escalateEverySec);
  const per = Number(
    o.damageMulPerStack != null ? o.damageMulPerStack : DEFAULT_MUL_PER_STACK,
  );
  if (!Number.isFinite(after) || after < 0) return null;
  if (!Number.isFinite(every) || every <= 0) return null;
  if (!Number.isFinite(per) || per < 0) return null;
  const maxRaw = Number(o.maxStacks);
  const maxStacks =
    Number.isFinite(maxRaw) && maxRaw > 0 ? Math.floor(maxRaw) : undefined;
  return {
    escalateAfterSec: after,
    escalateEverySec: every,
    damageMulPerStack: per,
    maxStacks,
  };
}

function assaultScriptRow(scriptId: string | undefined) {
  const id = (scriptId ??
    raidCatalog.assault.demoScriptId) as keyof typeof raidCatalog.assault.scripts;
  return raidCatalog.assault.scripts[id] ?? null;
}

/**
 * Knobs for the **current stage only**:
 * - assault wave → `waves[i].escalate` ?? script `escalate`
 * - boss (armed) → script `bossEscalate` ?? script `escalate`
 * - transition / unarmed → off
 */
export function escalateKnobsForState(
  state: RaidBattleState,
): RaidEscalateKnobs | null {
  const script = assaultScriptRow(state.encounter.scriptId);
  if (!script) return null;
  const enc = state.encounter;
  if (enc.mode === "transition") return null;
  if (enc.mode === "boss") {
    if (!enc.bossCombatArmed) return null;
    const bossRaw =
      "bossEscalate" in script ? script.bossEscalate : undefined;
    return parseEscalateKnobs(bossRaw ?? script.escalate);
  }
  if (enc.mode !== "assault") return null;
  const wave = script.waves[enc.waveIndex] as
    | { escalate?: unknown }
    | undefined;
  const waveRaw = wave && "escalate" in wave ? wave.escalate : undefined;
  return parseEscalateKnobs(waveRaw ?? script.escalate);
}

/** @deprecated prefer escalateKnobsForState — script default only. */
export function escalateKnobsFor(
  scriptId: string | undefined,
): RaidEscalateKnobs | null {
  const script = assaultScriptRow(scriptId);
  if (!script) return null;
  return parseEscalateKnobs(script.escalate);
}

/**
 * Stacks after stage clock starts:
 * t < after → 0; at after → 1; then +1 every `every` seconds.
 */
export function escalateStacksFromElapsed(
  elapsedSec: number,
  knobs: RaidEscalateKnobs,
): number {
  if (!(elapsedSec >= knobs.escalateAfterSec)) return 0;
  const n =
    1 +
    Math.floor(
      (elapsedSec - knobs.escalateAfterSec) / knobs.escalateEverySec,
    );
  if (knobs.maxStacks != null && knobs.maxStacks > 0) {
    return Math.min(knobs.maxStacks, n);
  }
  return Math.max(0, n);
}

export function escalateDamageMul(
  stacks: number,
  knobs: Pick<RaidEscalateKnobs, "damageMulPerStack">,
): number {
  if (stacks <= 0) return 1;
  return 1 + stacks * knobs.damageMulPerStack;
}

/** Clear clock — stacks read as 0 until beginEscalateScope. */
export function clearEscalateScope(state: RaidBattleState): void {
  state.encounter.escalateAnchorTime = -1;
}

/** Restart stage clock at current sim time (stacks start at 0). */
export function beginEscalateScope(state: RaidBattleState): void {
  state.encounter.escalateAnchorTime = state.time;
}

export function getEscalateStacks(state: RaidBattleState): number {
  const knobs = escalateKnobsForState(state);
  if (!knobs) return 0;
  const anchor = state.encounter.escalateAnchorTime;
  if (!(anchor >= 0)) return 0;
  return escalateStacksFromElapsed(state.time - anchor, knobs);
}

export function getEscalateDamageMul(state: RaidBattleState): number {
  const knobs = escalateKnobsForState(state);
  if (!knobs) return 1;
  return escalateDamageMul(getEscalateStacks(state), knobs);
}

/** Scale enemy→ally outgoing damage (AA + skills). No-op when stacks=0. */
export function scaleEnemyOutgoingDamage(
  state: RaidBattleState,
  baseDamage: number,
): number {
  if (!(baseDamage > 0)) return baseDamage;
  const mul = getEscalateDamageMul(state);
  if (mul === 1) return baseDamage;
  return baseDamage * mul;
}
